import { useEffect, useRef, useCallback } from "react";
import GeoJSON from "ol/format/GeoJSON";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { Feature } from "ol";
import { Geometry } from "ol/geom";
import type { StyleFunction } from "ol/style/Style";
import { useMap } from "../../hooks";

/**
 * 동적 스타일 GeoJSON 레이어 props — 피처별 스타일 함수·클릭 콜백 지원.
 *
 * `FeaturesStore`/`VectorSourceContext` 에 의존하지 않는 독립 레이어.
 */
export interface StyledGeoJsonLayerProps {
  geoJson: Record<string, any> | null;
  /**
   * GeoJSON 데이터의 좌표계
   * @default "EPSG:4326"
   */
  dataProjection?: string;
  /** 피처 스타일 함수 */
  style: StyleFunction;
  /**
   * @default 0
   */
  zIndex?: number;
  /** 피처 클릭 시 호출 */
  onFeatureClick?: (properties: Record<string, any>) => void;
}

/**
 * 독립적인 벡터 레이어로 GeoJSON을 렌더링하는 컴포넌트.
 * 동적 스타일 함수와 피처 클릭 이벤트를 지원.
 * GeoJsonLayer와 달리 FeaturesStore/VectorSourceContext에 의존하지 않음.
 */
export function StyledGeoJsonLayer({
  geoJson,
  dataProjection = "EPSG:4326",
  style,
  zIndex = 0,
  onFeatureClick,
}: StyledGeoJsonLayerProps) {
  const map = useMap();
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const onFeatureClickRef = useRef(onFeatureClick);
  onFeatureClickRef.current = onFeatureClick;

  const styleRef = useRef(style);
  styleRef.current = style;

  const handleClick = useCallback(
    (event: any) => {
      if (!onFeatureClickRef.current) return;

      map.forEachFeatureAtPixel(event.pixel, (feature) => {
        const properties = (feature as Feature<Geometry>).getProperties();
        onFeatureClickRef.current!(properties);
        return true;
      });
    },
    [map]
  );

  // 레이어 생성/파괴 — zIndex 를 의존성에 두면 zIndex 변경 시 레이어가 재생성되지만
  // 데이터 effect(geoJson 의존)는 재실행되지 않아 레이어가 빈 채로 남는 잠복 버그가 있었다.
  // zIndex 는 아래 전용 effect 로 분리해 재생성 없이 순서만 갱신한다(D8).
  useEffect(() => {
    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex,
      style: (feature, resolution) => styleRef.current(feature, resolution),
    });

    map.addLayer(vectorLayer);
    layerRef.current = vectorLayer;

    map.on("click", handleClick);

    return () => {
      map.un("click", handleClick);
      map.removeLayer(vectorLayer);
      layerRef.current = null;
    };
    // zIndex 는 전용 effect 에서 처리하므로 재생성 의존성에서 제외한다(D8).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, handleClick]);

  // zIndex 변경은 레이어 재생성 없이 순서만 갱신한다(빈 레이어 잠복 버그 수정 — D8).
  useEffect(() => {
    layerRef.current?.setZIndex(zIndex);
  }, [zIndex]);

  // GeoJSON 데이터 업데이트
  useEffect(() => {
    if (!layerRef.current) return;

    const source = layerRef.current.getSource();
    if (!source) return;

    source.clear();

    if (geoJson) {
      try {
        const mapProjection = map.getView().getProjection();
        const format = new GeoJSON({
          dataProjection,
          featureProjection: mapProjection,
        });
        const features = format.readFeatures(geoJson);
        source.addFeatures(features);
      } catch (e) {
        console.error("StyledGeoJsonLayer: GeoJSON 파싱 실패", e);
      }
    }
  }, [geoJson, dataProjection, map]);

  return null;
}
